<!-- Response feedback + copy (PCSA-13171). Only rendered for text llm responses
     (card responses excluded by the host), as the footer row inside the bubble.
     Thumbs-up / thumbs-down open the positive / negative bottom sheet. Hidden
     entirely once the conversation is guardrail-locked (AC 12).

     All three icons are the DESIGN'S OWN EXPORTS, inlined verbatim from
     iconsfromfigma/ (Material/Material/Content = copy, Material/Action = thumbs
     up, Material/Material/Action = thumbs down), at the 16px they are drawn at,
     and they are the SAME drawings in every state — the one thing that changes on
     submit is that a thumb's own silhouette is filled in (see the thumbs below).
     Inlined rather than referenced as <img>: the row paints its own state
     (muted -> navy outline on hover -> navy fill once submitted), which needs
     `currentColor`, and an <img> cannot inherit it. The exports' own #918F8F is
     therefore dropped from the paths and reached instead through the row's
     `color` chain, whose resting step IS that gray — see the SCSS.

     This replaces three Material Icons Round ligatures, one of which could not
     be drawn correctly at all: the face this app loads maps `thumb_up_off_alt`
     to the FILLED glyph, so the unrated outline had to be synthesised with
     -webkit-text-stroke. The design's own path needs no such trick. -->
<div class="orbi-feedback" *ngIf="!sessionLocked">
  <div class="orbi-feedback__actions">
    <button type="button" class="orbi-feedback__btn" aria-label="Copy response" (click)="onCopy()">
      <svg class="orbi-feedback__icon" width="14" height="16" viewBox="0 0 14 16" fill="none" aria-hidden="true" focusable="false">
        <path
          d="M9.88235 14.4H1.64706V4C1.64706 3.56 1.27647 3.2 0.823529 3.2C0.370588 3.2 0 3.56 0 4V14.4C0 15.28 0.741176 16 1.64706 16H9.88235C10.3353 16 10.7059 15.64 10.7059 15.2C10.7059 14.76 10.3353 14.4 9.88235 14.4ZM14 11.2V1.6C14 0.72 13.2588 0 12.3529 0H4.94118C4.03529 0 3.29412 0.72 3.29412 1.6V11.2C3.29412 12.08 4.03529 12.8 4.94118 12.8H12.3529C13.2588 12.8 14 12.08 14 11.2ZM12.3529 11.2H4.94118V1.6H12.3529V11.2Z"
          fill="currentColor"
        />
      </svg>
    </button>

    <!-- ONE drawing per thumb, in two weights: the design's export as an OUTLINE
         while the response is unrated (and while it is hovered — that state is a
         color change only), then the SAME silhouette FILLED once the sentiment is
         submitted (design review 08/27: "outline only on active, after submission
         it has to fill with that color").

         The filled `d` is not a second icon. It is this very export with its INNER
         contour deleted — the export draws the outline as an outer contour, an
         inner contour that punches the hole, and a detached arm bar, so dropping
         the middle subpath leaves the outer boundary untouched and the hole solid.
         Proven in Chrome rather than eyeballed: rasterised at 20x, `filled` is
         pixel-for-pixel the union of `outline` and the hole it drops (0 pixels of
         disagreement across 102,400 samples) with an identical 16x16 bbox. The
         spec re-derives one `d` from the other so the pair cannot drift.

         What this REPLACES is a swap to the filled `thumb_up` / `thumb_down`
         Material ligature, which the design review rejected (08/27: "why icon
         changed after like or unlike is done and why it has shrunk it shouldnt,
         use the same icon that normal state uses") — and both halves of that were
         real:
           - Different drawing. The ligature is Material's own filled thumb; the
             design ships no filled variant (docs/orbi/figma/img/ has exactly
             `thumb-up.svg` / `thumb-down.svg`, both outlines), so the swap
             substituted a different icon set mid-interaction. Filling the export
             is how the row gets a filled state without leaving the design's own
             drawing.
           - Smaller. A Material glyph's ink sits inside a 20/24 live area, so at
             the `font-size: 16px` that was meant to match, its ink measured
             15.31 x 13.03 against the export's 16 x 16 — 81% of the height
             (measured in Chrome against the app's own Material Icons Round face).
             The row's box never moved, which is why this read as the icon
             shrinking rather than the layout shifting.
         It also inherited `margin-top: -2px` from labcorp-bootstrap's global
         `.material-icons-round`, which this component never reset, so the
         submitted thumb sat 2px high in its 24px target as well. Staying inside
         the export retires all three at once.

         The `<svg>` itself never leaves the DOM — only the `<path>` inside it is
         conditional — so the icon's box is the same 16x16 through the change. -->
    <button
      type="button"
      class="orbi-feedback__btn"
      [class.orbi-feedback__btn--chosen]="isPositiveSubmitted"
      [attr.aria-label]="'Good response'"
      [attr.aria-pressed]="isPositiveSubmitted"
      [attr.disabled]="isLocked ? '' : null"
      (click)="openPositive()"
    >
      <svg class="orbi-feedback__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
        <!-- outer contour + hole + arm: the outline, unrated -->
        <path
          *ngIf="!isPositiveSubmitted"
          d="M5.73645 16C4.94863 16 4.30383 15.2568 4.30383 14.3486V6.10156C4.30383 5.664 4.45485 5.24199 4.71985 4.93652L8.67981 0.363281C8.88751 0.123857 9.16742 0 9.43958 0C9.71158 0.00011158 9.98394 0.115234 10.1915 0.354492C10.4494 0.651708 10.5561 1.07353 10.4845 1.48633L9.80481 5.26758H13.8507C15.3904 5.26758 16.4289 7.08318 15.8204 8.71777L13.4855 15.001C13.2634 15.6036 12.7479 16 12.1749 16H5.73645ZM12.1749 14.3486L14.5031 8.07422C14.639 7.71108 14.5242 7.4304 14.4454 7.29004C14.3667 7.14969 14.1873 6.91797 13.8507 6.91797H8.04212L8.40051 4.92871L8.83801 2.51758L5.73645 6.10156V14.3486H12.1749ZM1.43176 16C0.644098 15.9998 0.000123978 15.2567 0.000123978 14.3486V7.74414C0.000123978 6.83612 0.644098 6.09299 1.43176 6.09277C2.21959 6.09277 2.86438 6.83598 2.86438 7.74414V14.3486C2.86438 15.2568 2.21959 16 1.43176 16Z"
          fill="currentColor"
        />
        <!-- the same two outer subpaths, hole subpath dropped: filled, submitted -->
        <path
          *ngIf="isPositiveSubmitted"
          d="M5.73645 16C4.94863 16 4.30383 15.2568 4.30383 14.3486V6.10156C4.30383 5.664 4.45485 5.24199 4.71985 4.93652L8.67981 0.363281C8.88751 0.123857 9.16742 0 9.43958 0C9.71158 0.00011158 9.98394 0.115234 10.1915 0.354492C10.4494 0.651708 10.5561 1.07353 10.4845 1.48633L9.80481 5.26758H13.8507C15.3904 5.26758 16.4289 7.08318 15.8204 8.71777L13.4855 15.001C13.2634 15.6036 12.7479 16 12.1749 16H5.73645ZM1.43176 16C0.644098 15.9998 0.000123978 15.2567 0.000123978 14.3486V7.74414C0.000123978 6.83612 0.644098 6.09299 1.43176 6.09277C2.21959 6.09277 2.86438 6.83598 2.86438 7.74414V14.3486C2.86438 15.2568 2.21959 16 1.43176 16Z"
          fill="currentColor"
        />
      </svg>
    </button>

    <button
      type="button"
      class="orbi-feedback__btn"
      [class.orbi-feedback__btn--chosen]="isNegativeSubmitted"
      [attr.aria-label]="'Bad response'"
      [attr.aria-pressed]="isNegativeSubmitted"
      [attr.disabled]="isLocked ? '' : null"
      (click)="openNegative()"
    >
      <svg class="orbi-feedback__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
        <!-- outer contour + hole + arm: the outline, unrated -->
        <path
          *ngIf="!isNegativeSubmitted"
          d="M10.2616 0C11.0495 0 11.6942 0.743209 11.6942 1.65137V9.89844C11.6942 10.336 11.5442 10.758 11.2792 11.0635L7.31824 15.6367C7.11051 15.8761 6.83066 16 6.55847 16C6.28645 15.9999 6.01413 15.8847 5.80652 15.6455C5.54865 15.3483 5.44192 14.9265 5.51355 14.5137L6.19324 10.7324H2.14636C0.606574 10.7323 -0.431844 8.91674 0.176634 7.28223L2.5116 0.999023C2.73364 0.396337 3.25009 0 3.82312 0H10.2616ZM3.82312 1.65137L1.49499 7.92578C1.35898 8.28884 1.47285 8.56953 1.55163 8.70996C1.63041 8.85029 1.80981 9.08195 2.14636 9.08203H7.95593L7.59753 11.0713L7.16101 13.4824L10.2616 9.89844V1.65137H3.82312ZM14.5653 0C15.3532 2.93034e-05 15.9979 0.743227 15.9979 1.65137V8.25586C15.9979 9.164 15.3532 9.9072 14.5653 9.90723C13.7774 9.90723 13.1327 9.16402 13.1327 8.25586V1.65137C13.1327 0.743209 13.7774 0 14.5653 0Z"
          fill="currentColor"
        />
        <!-- the same two outer subpaths, hole subpath dropped: filled, submitted -->
        <path
          *ngIf="isNegativeSubmitted"
          d="M10.2616 0C11.0495 0 11.6942 0.743209 11.6942 1.65137V9.89844C11.6942 10.336 11.5442 10.758 11.2792 11.0635L7.31824 15.6367C7.11051 15.8761 6.83066 16 6.55847 16C6.28645 15.9999 6.01413 15.8847 5.80652 15.6455C5.54865 15.3483 5.44192 14.9265 5.51355 14.5137L6.19324 10.7324H2.14636C0.606574 10.7323 -0.431844 8.91674 0.176634 7.28223L2.5116 0.999023C2.73364 0.396337 3.25009 0 3.82312 0H10.2616ZM14.5653 0C15.3532 2.93034e-05 15.9979 0.743227 15.9979 1.65137V8.25586C15.9979 9.164 15.3532 9.9072 14.5653 9.90723C13.7774 9.90723 13.1327 9.16402 13.1327 8.25586V1.65137C13.1327 0.743209 13.7774 0 14.5653 0Z"
          fill="currentColor"
        />
      </svg>
    </button>

    <!-- Copy success is a toast (AC 1.2); only the retryable failure is inline. -->
    <span *ngIf="copyFailed" class="orbi-feedback__hint orbi-feedback__hint--error" role="alert"> Couldn't copy — try again </span>
  </div>
</div>
